"use client";

import { useCallback, useEffect } from "react";
import Image from "next/image";
import useEmblaCarousel from "embla-carousel-react";
import { motion, useReducedMotion } from "framer-motion";
import { ChevronLeft, ChevronRight, Image as ImageIcon } from "lucide-react";
import { cn } from "@/lib/utils";
import { useDictionary } from "@/lib/useDictionary";

type PartnerType = "brand" | "ads";

type CarouselImageValue =
  | {
      asset: {
        url: string;
      };
    }
  | string
  | null
  | undefined;

interface CarouselImage {
  _id: string;
  title: string;
  description?: string;
  image?: CarouselImageValue;
  alt?: string;
  link?: string;
  type?: PartnerType;
}

const defaultImages: CarouselImage[] = [
  {
    _id: "1",
    title: "數位簡報室",
    description: "Dennis",
    image: "/people/DENNIS.jpg",
    alt: "數位簡報室創辦人 Dennis",
    type: "ads",
  },
  {
    _id: "2",
    title: "lihi 短網址",
    description: "貝克菜",
    image: "/people/BK.png",
    alt: "lihi 短網址創辦人 貝克菜",
    type: "brand",
  },
  {
    _id: "3",
    title: "圭話行銷",
    description: "小圭老師",
    image: "/people/TURTLE.jpg",
    alt: "圭話行銷創辦人 小圭老師",
    type: "ads",
  },
  {
    _id: "5",
    title: "國泰金控",
    description: "數位規劃部 LEO",
    image: "/people/LEO.jpg",
    alt: "國泰金控數位規劃部 LEO",
    type: "brand",
  },
  {
    _id: "6",
    title: "宇動數位",
    description: "Bibi",
    image: "/people/BIBI.jpg",
    alt: "宇動數位 Bibi",
    type: "ads",
  },
  {
    _id: "7",
    title: "先拜顧問",
    description: "Hugo Chen",
    image: "/people/HUGO.jpg",
    alt: "先拜顧問 Hugo Chen",
    type: "ads",
  },
  {
    _id: "8",
    title: "CYBERBIZ",
    description: "台灣主流開店平台",
    image: "/people/CYBERBIZ.png",
    alt: "CYBERBIZ",
    type: "brand",
  },
  {
    _id: "9",
    title: "SHOPIFY",
    description: "國際主流開店平台",
    image: "/people/SHOPIFY.png",
    alt: "SHOPIFY",
    type: "brand",
  },
  {
    _id: "10",
    title: "lihi 短網址",
    description: "台灣最大短網址服務",
    image: "/people/LIHI.jpg",
    alt: "lihi 短網址",
    type: "brand",
  },
  {
    _id: "11",
    title: "尬廣數位",
    description: "LINE 鑽級夥伴",
    image: "/people/god.jpg",
    alt: "尬廣數位",
    type: "ads",
  },
  {
    _id: "12",
    title: "Daisy",
    description: "品牌行銷顧問",
    image: "/people/Daisy.jpg",
    alt: "Daisy",
    type: "ads",
  },
];

interface PhotoCarouselProps {
  images?: CarouselImage[];
  className?: string;
}

// 堆疊長條的資料來源：各段相加即為總用戶數，不再另外寫死總數。
const partnerSegments = [
  {
    key: "brand",
    type: "brand",
    count: 1900,
    barClass: "bg-[#3b82f6]",
    dotClass: "bg-[#2563eb]",
    previewCount: 10,
  },
  {
    key: "ads",
    type: "ads",
    count: 100,
    barClass: "bg-[#14b8a6]",
    dotClass: "bg-[#0d9488]",
    previewCount: 10,
  },
] as const;

const totalUsers = partnerSegments.reduce(
  (sum, segment) => sum + segment.count,
  0,
);

function getImageSrc(image?: CarouselImageValue) {
  return typeof image === "string" ? image : image?.asset?.url;
}

export default function PhotoCarousel({
  images = defaultImages,
  className,
}: PhotoCarouselProps) {
  const dict = useDictionary();
  const prefersReducedMotion = useReducedMotion();

  const segmentLabels: Record<(typeof partnerSegments)[number]["key"], string> =
    {
      brand: dict.photoCarousel.dimensions_brand,
      ads: dict.photoCarousel.dimensions_ads,
    };

  const chartCards = partnerSegments.map((segment) => ({
    ...segment,
    label: segmentLabels[segment.key],
    share: Math.round((segment.count / totalUsers) * 100),
    partners: images.filter((partner) => partner.type === segment.type),
  }));

  const chartLabel = `${dict.photoCarousel.dimensions_total} ${totalUsers}：${chartCards
    .map((item) => `${item.label} ${item.count} (${item.share}%)`)
    .join("、")}`;

  const [emblaRef, emblaApi] = useEmblaCarousel({
    loop: true,
    align: "start",
    slidesToScroll: 1,
  });

  const scrollPrev = useCallback(() => {
    if (emblaApi) emblaApi.scrollPrev();
  }, [emblaApi]);

  const scrollNext = useCallback(() => {
    if (emblaApi) emblaApi.scrollNext();
  }, [emblaApi]);

  useEffect(() => {
    if (!emblaApi) return;
    if (prefersReducedMotion) return;

    const autoplay = setInterval(() => {
      if (document.visibilityState !== "visible") return;
      emblaApi.scrollNext();
    }, 4000);

    return () => {
      clearInterval(autoplay);
    };
  }, [emblaApi, prefersReducedMotion]);

  // 各段寬度用 flex-grow 依比例分配，間隙才不會把總寬撐超過 100%。
  const renderBar = (heightClass: string) => (
    <motion.div
      role="img"
      aria-label={chartLabel}
      initial={prefersReducedMotion ? false : { scaleX: 0 }}
      whileInView={prefersReducedMotion ? undefined : { scaleX: 1 }}
      viewport={{ once: true }}
      transition={{ duration: 0.7, ease: "easeOut" }}
      className={cn("flex origin-left gap-[3px]", heightClass)}
    >
      {chartCards.map((item, index) => (
        <div
          key={item.key}
          style={{ flexGrow: item.count }}
          className={cn(
            "basis-0",
            item.barClass,
            index === 0
              ? "rounded-l-full rounded-r-sm"
              : "rounded-l-sm rounded-r-full",
          )}
        />
      ))}
    </motion.div>
  );

  return (
    <section
      className={cn(
        "relative overflow-hidden bg-[radial-gradient(circle_at_top_left,_rgba(125,211,252,0.28),_transparent_28%),linear-gradient(180deg,#eef6ff_0%,#dcecff_48%,#eef6ff_100%)] py-20",
        className,
      )}
    >
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="hidden md:block">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="mx-auto mb-12 max-w-3xl text-center"
          >
            <h2 className="flex flex-wrap items-baseline justify-center gap-x-3 gap-y-2 text-[var(--blue-900)]">
              <span className="hero-ink text-6xl font-black sm:text-7xl lg:text-7xl">
                {totalUsers}
              </span>
              <span className="text-3xl font-bold sm:text-4xl lg:text-5xl">
                + {dict.photoCarousel.heading}
              </span>
            </h2>
          </motion.div>

          <div className="rounded-[1.5rem] border border-white/70 bg-white/50 p-6 shadow-[0_25px_80px_rgba(56,132,255,0.12)] backdrop-blur lg:p-8">
            {renderBar("h-14")}

            <div className="mt-2.5 flex items-baseline justify-between text-xs font-medium text-[var(--blue-900)]/55">
              <span>0</span>
              <span>
                {dict.photoCarousel.dimensions_total} {totalUsers}
              </span>
            </div>

            <div className="mt-7 grid gap-5 sm:grid-cols-2">
              {chartCards.map((item, index) => {
                const visiblePartners = item.partners.slice(
                  0,
                  item.previewCount,
                );
                const hiddenCount =
                  item.partners.length - visiblePartners.length;

                return (
                  <motion.div
                    key={item.key}
                    initial={
                      prefersReducedMotion ? false : { opacity: 0, y: 16 }
                    }
                    whileInView={
                      prefersReducedMotion ? undefined : { opacity: 1, y: 0 }
                    }
                    viewport={{ once: true }}
                    transition={{ duration: 0.45, delay: 0.2 + index * 0.1 }}
                    className="rounded-[1.25rem] border border-white/80 bg-white/70 p-5 shadow-[0_10px_30px_rgba(56,132,255,0.08)]"
                  >
                    <div className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-2">
                      <div className="flex items-center gap-2.5">
                        <span
                          className={cn(
                            "h-3 w-3 shrink-0 rounded-full",
                            item.dotClass,
                          )}
                        />
                        <span className="text-base font-semibold tracking-[0.05em] text-[var(--blue-900)]">
                          {item.label}
                        </span>
                      </div>
                      <div className="flex items-baseline gap-2">
                        <span className="text-3xl font-black text-[var(--blue-900)]">
                          {item.count}+
                        </span>
                        <span className="text-sm font-medium text-[var(--blue-900)]/55">
                          {item.share}%
                        </span>
                      </div>
                    </div>

                    {visiblePartners.length > 0 ? (
                      <div className="mt-4 flex flex-wrap items-center gap-2">
                        {visiblePartners.map((partner) => {
                          const partnerImage = getImageSrc(partner.image);

                          return partnerImage ? (
                            <div
                              key={partner._id}
                              className="relative h-11 w-11 overflow-hidden rounded-full border-2 border-white bg-white shadow-md"
                              title={partner.alt || partner.title}
                            >
                              <Image
                                src={partnerImage}
                                alt={partner.alt || partner.title}
                                fill
                                sizes="44px"
                                className="object-cover"
                              />
                            </div>
                          ) : (
                            <div
                              key={partner._id}
                              className="flex h-11 w-11 items-center justify-center rounded-full border-2 border-white bg-white text-slate-400 shadow-md"
                              title={partner.alt || partner.title}
                            >
                              <ImageIcon className="h-4 w-4" />
                            </div>
                          );
                        })}

                        {hiddenCount > 0 ? (
                          <div className="flex h-11 min-w-11 items-center justify-center rounded-full border-2 border-white bg-[var(--blue-900)] px-2 text-xs font-bold text-white shadow-md">
                            +{hiddenCount}
                          </div>
                        ) : null}
                      </div>
                    ) : null}
                  </motion.div>
                );
              })}
            </div>
          </div>
        </div>

        <div className="md:hidden">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="mx-auto mb-10 max-w-md"
          >
            <h2 className="flex flex-wrap items-baseline justify-center gap-x-2 text-center text-[var(--blue-900)]">
              <span className="hero-ink text-5xl font-black">
                {totalUsers}
              </span>
              <span className="text-2xl font-bold">
                + {dict.photoCarousel.heading}
              </span>
            </h2>

            <div className="mt-5 rounded-2xl border border-white/70 bg-white/60 p-4 shadow-[0_10px_30px_rgba(56,132,255,0.10)] backdrop-blur">
              {renderBar("h-3.5")}

              <dl className="mt-4 space-y-2.5">
                {chartCards.map((item) => (
                  <div key={item.key} className="flex items-center gap-2.5">
                    <span
                      className={cn(
                        "h-2.5 w-2.5 shrink-0 rounded-full",
                        item.dotClass,
                      )}
                    />
                    <dt className="flex-1 text-sm font-medium text-[var(--blue-900)]">
                      {item.label}
                    </dt>
                    <dd className="text-sm font-bold text-[var(--blue-900)]">
                      {item.count}+
                    </dd>
                    <dd className="w-9 text-right text-xs font-medium text-[var(--blue-900)]/55">
                      {item.share}%
                    </dd>
                  </div>
                ))}
              </dl>
            </div>
          </motion.div>

          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="mb-6 text-center text-3xl font-bold text-[var(--blue-900)]"
          >
            快客推薦夥伴
          </motion.h2>

          <div className="relative">
            <button
              onClick={scrollPrev}
              className="absolute left-0 top-1/2 z-10 h-10 w-10 -translate-x-4 -translate-y-1/2 rounded-full bg-white shadow-lg transition-colors hover:bg-[var(--light-blue)]"
              aria-label="Previous slide"
            >
              <ChevronLeft className="mx-auto h-5 w-5 text-[var(--blue-700)]" />
            </button>
            <button
              onClick={scrollNext}
              className="absolute right-0 top-1/2 z-10 h-10 w-10 translate-x-4 -translate-y-1/2 rounded-full bg-white shadow-lg transition-colors hover:bg-[var(--light-blue)]"
              aria-label="Next slide"
            >
              <ChevronRight className="mx-auto h-5 w-5 text-[var(--blue-700)]" />
            </button>

            <div className="overflow-hidden" ref={emblaRef}>
              <div className="flex -ml-4 sm:-ml-6">
                {images.map((item) => {
                  const imageSrc = getImageSrc(item.image);

                  return (
                    <div
                      key={item._id}
                      className="min-w-0 flex-[0_0_50%] pl-4 sm:flex-[0_0_33.33%] sm:pl-6 lg:flex-[0_0_20%]"
                    >
                      <motion.div
                        whileHover={
                          prefersReducedMotion ? undefined : { scale: 1.03 }
                        }
                        className="flex h-44 flex-col items-center justify-center gap-3 rounded-xl bg-white p-5 shadow-md transition-all duration-300 hover:shadow-lg"
                      >
                        {imageSrc ? (
                          <div
                            className="relative h-28 w-24 overflow-hidden rounded bg-gray-100"
                            title={item.alt || item.title}
                          >
                            <Image
                              src={imageSrc}
                              alt={item.alt || item.title}
                              fill
                              sizes="80px"
                              className="object-cover"
                            />
                          </div>
                        ) : (
                          <div
                            className="flex h-24 w-24 items-center justify-center rounded-full bg-gray-100 text-gray-400"
                            title={item.alt || item.title}
                          >
                            <ImageIcon className="h-7 w-7" />
                          </div>
                        )}

                        <div className="text-center leading-tight">
                          <div className="text-sm font-semibold text-gray-900">
                            {item.title}
                          </div>
                          {item.description ? (
                            <div className="mt-1 text-xs text-gray-500">
                              {item.description}
                            </div>
                          ) : null}
                        </div>
                      </motion.div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
